import type { MfaMethod, Organisation } from "src/types/commonTypes";
import { API_PREFIX } from "src/config/Config";

export const COOKIE_API_BASE = API_PREFIX;
export const AUTH_API_BASE = `${COOKIE_API_BASE}/auth`;
export const GRAPHQL_PROXY_PATH = "/graphql";
export const INTERNAL_GRAPHQL_PROXY_PATH = "/internal-graphql";
export const SESSION_AUTHORIZATION = "Session";
export const WEB_REQUEST_MARKER = "spendable-web";

export const AUTH_ERROR_CODE = {
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  NO_SESSION: "NO_SESSION",
  NOT_AUTHORIZED: "NOT_AUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  FORBIDDEN_ORIGIN: "FORBIDDEN_ORIGIN",
  TOO_MANY_REQUESTS: "TOO_MANY_REQUESTS",
  USER_NOT_CONFIRMED: "USER_NOT_CONFIRMED",
  PASSWORD_RESET_REQUIRED: "PASSWORD_RESET_REQUIRED",
  CODE_MISMATCH: "CODE_MISMATCH",
  CODE_EXPIRED: "CODE_EXPIRED",
  INVALID_CODE: "INVALID_CODE",
  INVALID_PASSWORD: "INVALID_PASSWORD",
  INVALID_PARAMETER: "INVALID_PARAMETER",
  USERNAME_EXISTS: "USERNAME_EXISTS",
  ALIAS_EXISTS: "ALIAS_EXISTS",
  USER_VALIDATION_FAILED: "USER_VALIDATION_FAILED",
  CHALLENGE_EXPIRED: "CHALLENGE_EXPIRED",
  UNSUPPORTED_CHALLENGE: "UNSUPPORTED_CHALLENGE",
  INVALID_CLIENT: "INVALID_CLIENT",
  CODE_DELIVERY_FAILED: "CODE_DELIVERY_FAILED",
  MFA_ENROLMENT_REQUIRED: "MFA_ENROLMENT_REQUIRED"
} as const;

export class AuthApiError extends Error {
  code: string;
  status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "AuthApiError";
    this.code = code;
    this.status = status;
  }
}

export type AuthChallengeName =
  | "SMS_MFA"
  | "SMS_OTP"
  | "EMAIL_OTP"
  | "SOFTWARE_TOKEN_MFA"
  | "NEW_PASSWORD_REQUIRED"
  | "CUSTOM_CHALLENGE"
  | "SELECT_MFA_TYPE";

export type SessionView = {
  user_id: string;
  cognito_sub: string;
  cognito_username: string;
  email: string;
  preferred_username?: string | null;
  name?: string | null;
  family_name?: string | null;
  phone_number?: string | null;
  current_role?: string | null;
  current_organisation?: string | null;
  default_organisation?: string | null;
  available_relations: Organisation[];
  subscription_ids: string[];
  identity_provider?: string | null;
  token_expires_at?: string | number | null;
  expires_at?: string | number | null;
  mfa_enrolment_required?: boolean;
};

export type AuthChallenge = {
  status: "CHALLENGE";
  challenge: AuthChallengeName;
  challengeToken: string;
  codeDeliveryDestination?: string | null;
  codeDeliveryMedium?: string | null;
  requiredAttributes?: string[];
};

export type AuthSessionResult = { status: "OK"; session: SessionView };

export type LoginResult = AuthSessionResult | AuthChallenge;

export type SignUpResult = {
  status: "DONE" | "CONFIRM_SIGN_UP";
  userConfirmed: boolean;
  codeDeliveryDestination?: string | null;
  codeDeliveryMedium?: string | null;
};

export type MfaPreferenceView = {
  enabled: MfaMethod[];
  preferred: MfaMethod | null;
};

export type TotpSetupView = {
  secret: string;
  otpauthUri: string;
};

type AuthRequest = {
  method?: "GET" | "POST" | "PUT";
  body?: Record<string, unknown>;
};

const toAuthApiError = async (response: Response): Promise<AuthApiError> => {
  let payload: { code?: unknown; message?: unknown } = {};

  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  const code = typeof payload.code === "string" ? payload.code : String(response.status);
  const message = typeof payload.message === "string" ? payload.message : response.statusText || "Request failed";

  return new AuthApiError(response.status, code, message);
};

export const authRequest = async <T = void>(path: string, { method = "GET", body }: AuthRequest = {}): Promise<T> => {
  const response = await fetch(`${AUTH_API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": WEB_REQUEST_MARKER
    },
    body: body ? JSON.stringify(body) : undefined
  });

  if (!response.ok) throw await toAuthApiError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
};

export const login = (email: string, password: string) =>
  authRequest<LoginResult>("/login", { method: "POST", body: { email, password } });

export const answerChallenge = (challengeToken: string, answer: string) =>
  authRequest<LoginResult>("/challenge", { method: "POST", body: { challengeToken, answer } });

export const getSession = () => authRequest<SessionView>("/session");

let sessionCheck: Promise<SessionView> | null = null;

// Concurrent 401s share one session check; resolves while the cookie is still valid, rejects when it is gone.
export const ensureSession = () => {
  if (!sessionCheck) {
    sessionCheck = getSession().finally(() => {
      sessionCheck = null;
    });
  }
  return sessionCheck;
};

export const refreshAuthSession = () => authRequest<SessionView>("/refresh", { method: "POST" });

export const logoutSession = () => authRequest("/logout", { method: "POST" });

export const forgotPassword = (email: string) => authRequest("/password/forgot", { method: "POST", body: { email } });

export const resetPasswordWithCode = (email: string, code: string, newPassword: string) =>
  authRequest("/password/reset", { method: "POST", body: { email, code, newPassword } });

export const changePassword = (currentPassword: string, newPassword: string) =>
  authRequest("/password/change", { method: "POST", body: { currentPassword, newPassword } });

export const verifyPassword = (password: string) =>
  authRequest("/password/verify", { method: "POST", body: { password } });

export type SignUpRequest = {
  username: string;
  password: string;
  email: string;
  name: string;
  family_name: string;
  phone_number?: string;
};

export const signUp = (data: SignUpRequest) => authRequest<SignUpResult>("/signup", { method: "POST", body: data });

export const confirmSignUp = (username: string, code: string) =>
  authRequest("/signup/confirm", { method: "POST", body: { username, code } });

export const getSsoStartUrl = (clientId: string, identityProvider: string | undefined, returnTo: string) => {
  const params = new URLSearchParams({ client_id: clientId });

  if (identityProvider) {
    params.set("identity_provider", identityProvider);
  }
  params.set("return_to", returnTo);

  return `${AUTH_API_BASE}/sso/start?${params.toString()}`;
};

export const getMfaPreference = () => authRequest<MfaPreferenceView>("/mfa");

export const setupTotp = () => authRequest<TotpSetupView>("/mfa/totp/setup", { method: "POST" });

export const verifyTotp = (code: string) => authRequest("/mfa/totp/verify", { method: "POST", body: { code } });

export const setMfaPreference = (method: MfaMethod | null) =>
  authRequest("/mfa/preference", { method: "PUT", body: { method } });
